// PT SOEDJANA ABADI INDONESIA — Industrial Monitoring Dashboard
// Stack: React CRA + Tailwind + Recharts + Axios

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  ReferenceLine, Legend,
} from "recharts";
import {
  Droplets, Thermometer, Wind, Activity, Camera,
  Folder, FileText, ChevronRight, RefreshCw, AlertTriangle,
  Wifi, WifiOff, Clock, Shield, Zap, FolderOpen,
  TrendingUp, TrendingDown, Minus, Maximize2, Globe,
  Layers, File, Bell, ChevronLeft, X, Play, Pause,
  Volume2, VolumeX, ZoomIn, Download, Calendar, Filter,
  ArrowLeft, Settings, Eye, BarChart2,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   FONT INJECTION
═══════════════════════════════════════════════════════════ */
const GLOBAL_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; overflow: hidden; }
  body { font-family: 'DM Sans', sans-serif; background: #0b1120; color: #e2e8f0; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: rgba(255,255,255,0.04); border-radius: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.14); border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
  @keyframes pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(.85)} }
  @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes slideIn { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:translateX(0)} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(1) opacity(.5); }
`;

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════ */
const T = {
  bg:       "#0b1120",
  surface:  "rgba(255,255,255,0.055)",
  surfaceHover: "rgba(255,255,255,0.09)",
  border:   "rgba(255,255,255,0.1)",
  borderAccent: "rgba(251,191,36,0.4)",
  text:     "#e2e8f0",
  textMuted:"#64748b",
  textDim:  "#334155",
  cyan:     "#38bdf8",
  amber:    "#fbbf24",
  green:    "#4ade80",
  red:      "#f87171",
  indigo:   "#818cf8",
  orange:   "#fb923c",
  purple:   "#c084fc",
  blue:     "#60a5fa",
};

const SENSOR_META = {
  water_flow:    { label: "Water Flow",    unit: "L/min", color: T.blue,   icon: Wind,        base: 24, variance: 6,   threshold: 35, gradId: "gWF" },
  water_level:   { label: "Water Level",   unit: "m",     color: T.cyan,   icon: Droplets,    base: 4.2, variance: 1.2, threshold: 5.5, gradId: "gWL" },
  temperature:   { label: "Room Temp",     unit: "°C",    color: T.orange, icon: Thermometer, base: 28.4, variance: 3,  threshold: 32,  gradId: "gTM" },
  water_quality: { label: "Water Quality", unit: "",      color: T.purple, icon: Activity,    base: 7.2, variance: 1,   threshold: null, gradId: "gWQ" },
};

/* ═══════════════════════════════════════════════════════════
   MOCK DATA GENERATION — rich history
═══════════════════════════════════════════════════════════ */
const genHistory = (base, variance, days = 30, ptPerDay = 48) => {
  const now = Date.now();
  const total = days * ptPerDay;
  const step = (days * 86400000) / total;
  let v = base;
  return Array.from({ length: total }, (_, i) => {
    v = Math.max(0, v + (Math.random() - 0.495) * variance * 0.35);
    const ts = now - (total - i) * step;
    const d  = new Date(ts);
    return {
      ts,
      date:  d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" }),
      time:  d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }),
      datetime: d.toLocaleString("id-ID", { day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit" }),
      v: +v.toFixed(3),
    };
  });
};

const HISTORY = {
  water_flow:  genHistory(24, 6,  30, 48),
  water_level: genHistory(0.4, 1, 30, 48),
  temperature: genHistory(26.4, 2, 30, 48),
};

/* ═══════════════════════════════════════════════════════════
   API
═══════════════════════════════════════════════════════════ */
const fetchSensor = async (stationId, sensorType) => {
  const { data } = await axios.request({
    method: "GET",
    url: "https://api.ideotekno.cloud/sensor-monitoring/hydro-meteorology",
    params: { station_id: stationId, sensor_type: sensorType },
  });
  return data;
};

/* ═══════════════════════════════════════════════════════════
   ARCHIVE DATA
═══════════════════════════════════════════════════════════ */
const ARCHIVE = [
  { id: 1, name: "Data Umum Pabrik",   type: "pdf",  size: "", date: "10 Jan" },
  { id: 2, name: "Data Teknis & Desain Pabrik",   type: "pdf",  size: "", date: "10 Jan" },
  { id: 3, name: "Denah", type: "folder", children: [
    { id: 31, name: "Workshop Room", type: "pdf", size: "", date: "15 Apr", link:"/dashboard/images/WORKSHOP ROOM.pdf#toolbar=0" },
    { id: 32, name: "Warehouse Room - RAW",  type: "pdf", size: "", date: "10 Apr" },
    { id: 33, name: "Warehouse Room - Packaging",  type: "pdf", size: "", date: "10 Apr" },
    { id: 34, name: "Cordial Room - RAW",  type: "pdf", size: "", date: "10 Apr" },
  ]},
  
];

const CAMS = [
  { id: 1, label: "CAM 01 Loading Yard",    zone: "Loading Yard", link:"images/cctv-pabrik-loading-yard.mp4"   },
  { id: 2, label: "CAM 02 Warehouse",    zone: "Warehouse" , link:"images/cam-2-warehouse.mp4"  },
  { id: 3, label: "CAM 03 Corridor",    zone: "Corridor" , link:"images/cctv-pabrik-corridor-1.mp4"  },
  { id: 6, label: "CAM 06 Steam Room 2", zone: "Steam Room" , link:"images/cam 6 steam room 2-1.mp4" },
  { id: 5, label: "CAM 05 Steam Room", zone: "Steam Room" , link:"images/cam 5 steam room.mp4" },
  { id: 4, label: "CAM 04 Steam Room 3", zone: "Steam Room" , link:"images/cam 4 steam room 3.mp4" },
];

const FILE_CLR = { pdf: "#f87171", xlsx: "#4ade80", csv: "#34d399", docx: "#60a5fa", folder: "#fbbf24", dwg: "#c084fc" };

/* ═══════════════════════════════════════════════════════════
   PERIOD HELPERS
═══════════════════════════════════════════════════════════ */
const PERIODS = [
  { key: "1h",  label: "1H",  ms: 3600000 },
  { key: "6h",  label: "6H",  ms: 21600000 },
  { key: "1d",  label: "1D",  ms: 86400000 },
  { key: "7d",  label: "7D",  ms: 604800000 },
  { key: "30d", label: "30D", ms: 2592000000 },
  { key: "custom", label: "Custom", ms: null },
];

const filterByPeriod = (data, periodMs, customFrom, customTo) => {
  if (!periodMs) {
    const from = customFrom ? new Date(customFrom).getTime() : 0;
    const to   = customTo   ? new Date(customTo).getTime()   : Date.now();
    return data.filter(d => d.ts >= from && d.ts <= to);
  }
  const cutoff = Date.now() - periodMs;
  return data.filter(d => d.ts >= cutoff);
};

const downsample = (data, max = 200) => {
  if (data.length <= max) return data;
  const step = Math.ceil(data.length / max);
  return data.filter((_, i) => i % step === 0);
};

/* ═══════════════════════════════════════════════════════════
   SHARED UI ATOMS
═══════════════════════════════════════════════════════════ */
const GlassCard = ({ children, style, className, onClick }) => (
  <div
    onClick={onClick}
    className={className}
    style={{
      background: T.surface,
      backdropFilter: "blur(18px)",
      WebkitBackdropFilter: "blur(18px)",
      border: `1px solid ${T.border}`,
      borderRadius: 16,
      boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
      ...style,
    }}
  >
    {children}
  </div>
);

const Chip = ({ children, color = T.textMuted, bg, style }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 10, fontWeight: 600, letterSpacing: "0.07em",
    padding: "2px 8px", borderRadius: 20,
    background: bg ?? `${color}18`,
    color, border: `1px solid ${color}30`,
    fontFamily: "'JetBrains Mono', monospace",
    ...style,
  }}>
    {children}
  </span>
);

const PeriodSelector = ({ period, onChange }) => (
  <div style={{ display: "flex", gap: 4, background: "rgba(0,0,0,0.3)", borderRadius: 10, padding: 3, border: `1px solid ${T.border}` }}>
    {PERIODS.map(p => (
      <button key={p.key} onClick={() => onChange(p.key)}
        style={{
          padding: "4px 10px", borderRadius: 7, border: "none", cursor: "pointer",
          fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace",
          transition: "all 0.18s",
          background: period === p.key ? T.amber : "transparent",
          color: period === p.key ? "#0b1120" : T.textMuted,
          boxShadow: period === p.key ? `0 2px 8px ${T.amber}55` : "none",
        }}>
        {p.label}
      </button>
    ))}
  </div>
);

const SectionHeader = ({ label, accent, children }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 3, height: 16, borderRadius: 3, background: accent ?? `linear-gradient(180deg,${T.cyan},${T.indigo})`, boxShadow: `0 0 8px ${accent ?? T.cyan}` }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: "0.1em", textTransform: "uppercase" }}>{label}</span>
    </div>
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label, unit, color }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "rgba(10,14,26,0.92)", backdropFilter: "blur(10px)", border: `1px solid ${color}44`, borderRadius: 10, padding: "8px 14px" }}>
      <p style={{ color: T.textMuted, fontSize: 10, marginBottom: 4, fontFamily: "monospace" }}>{label}</p>
      <p style={{ color, fontWeight: 700, fontSize: 15, fontFamily: "'JetBrains Mono', monospace" }}>
        {payload[0].value?.toFixed(3)} <span style={{ color: T.textDim, fontWeight: 400, fontSize: 10 }}>{unit}</span>
      </p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   LEFT PANEL — SENSOR SPARKLINE CARD
═══════════════════════════════════════════════════════════ */
function SensorMiniCard({ sensorKey, liveData, onOpenDetail }) {
  const meta = SENSOR_META[sensorKey];
  const Icon = meta.icon;
  const last  = liveData?.at(-1)?.v ?? 0;
  const prev  = liveData?.at(-2)?.v ?? last;
  const delta = +(last - prev).toFixed(3);
  const isAlert = meta.threshold != null && last > meta.threshold;
  const TIcon = delta > 0.02 ? TrendingUp : delta < -0.02 ? TrendingDown : Minus;
  const tColor = delta > 0.02 ? T.red : delta < -0.02 ? T.green : T.textMuted;

  return (
    <GlassCard
      style={{
        marginBottom: 10, overflow: "hidden", cursor: "pointer",
        border: `1px solid ${isAlert ? `${T.red}55` : T.border}`,
        boxShadow: isAlert ? `0 6px 24px ${T.red}18` : "0 6px 24px rgba(0,0,0,0.18)",
        transition: "all 0.22s",
      }}
      onClick={() => onOpenDetail(sensorKey)}
      onMouseEnter={e => { e.currentTarget.style.background = T.surfaceHover; e.currentTarget.style.borderColor = meta.color + "55"; }}
      onMouseLeave={e => { e.currentTarget.style.background = T.surface; e.currentTarget.style.borderColor = isAlert ? `${T.red}55` : T.border; }}
    >
      <div style={{ padding: "12px 14px 6px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: `${meta.color}1a`, border: `1px solid ${meta.color}33`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon size={13} color={meta.color} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: "0.02em" }}>{meta.label}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            {isAlert && <AlertTriangle size={11} color={T.red} style={{ animation: "pulse-dot 1.5s infinite" }} />}
            <Eye size={11} color={T.textDim} />
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
          <span style={{ fontSize: 24, fontWeight: 800, color: meta.color, lineHeight: 1, fontFamily: "'JetBrains Mono', monospace" }}>
            {last.toFixed(1)}
          </span>
          <span style={{ fontSize: 12, color: T.textMuted, marginBottom: 2 }}>{meta.unit}</span>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 3, marginBottom: 3 }}>
            <TIcon size={11} color={tColor} />
            <span style={{ fontSize: 10, color: tColor, fontFamily: "monospace" }}>{Math.abs(delta).toFixed(2)}</span>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={50}>
        <AreaChart data={liveData?.slice(-30)} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={meta.gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={meta.color} stopOpacity={0.4} />
              <stop offset="95%" stopColor={meta.color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotoneX" dataKey="v" stroke={meta.color} strokeWidth={1.8} fill={`url(#${meta.gradId})`} dot={false} isAnimationActive={false} />
          {meta.threshold && <ReferenceLine y={meta.threshold} stroke={meta.color} strokeDasharray="3 3" strokeOpacity={0.35} />}
        </AreaChart>
      </ResponsiveContainer>
    </GlassCard>
  );
}

/* ═══════════════════════════════════════════════════════════
   WATER QUALITY MINI PANEL
═══════════════════════════════════════════════════════════ */
function WaterQualityMini({ wq, onOpenDetail }) {
  const rows = [
    { k: "pH",    v: wq.pH ?? 7.1,              u: "",      c: T.cyan   },
    { k: "Turb.", v: wq.turbidity ?? 3.2,        u: "NTU",  c: T.orange },
    { k: "DO",    v: wq.dissolved_oxygen ?? 8.4, u: "mg/L", c: T.green  },
    { k: "Cond.", v: wq.conductivity ?? 412,      u: "µS",   c: T.purple },
  ];
  return (
    <GlassCard style={{ overflow: "hidden", cursor: "pointer" }}
      onClick={() => onOpenDetail("water_quality")}
      onMouseEnter={e => { e.currentTarget.style.background = T.surfaceHover; e.currentTarget.style.borderColor = T.purple + "55"; }}
      onMouseLeave={e => { e.currentTarget.style.background = T.surface; e.currentTarget.style.borderColor = T.border; }}
    >
      <div style={{ padding: "11px 14px 8px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: `${T.purple}1a`, border: `1px solid ${T.purple}33`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Activity size={13} color={T.purple} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted }}>Water Quality</span>
        </div>
        <Eye size={11} color={T.textDim} />
      </div>
      <div style={{ padding: "10px 12px 12px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
        {rows.map(r => (
          <div key={r.k} style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{r.k}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: r.c, fontFamily: "monospace" }}>{r.v}<span style={{ fontSize: 9, color: T.textDim, marginLeft: 2 }}>{r.u}</span></span>
            </div>
            <div style={{ height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.min(100, (r.v / 10) * 100)}%`, background: r.c, borderRadius: 3, boxShadow: `0 0 6px ${r.c}55`, transition: "width 0.8s ease" }} />
            </div>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

/* ═══════════════════════════════════════════════════════════
   ARCHIVE ITEM (recursive)
═══════════════════════════════════════════════════════════ */
function ArchiveItem({ item, depth = 0, onOpen }) {
  const [open, setOpen] = useState(false);
  const isFolder = item.type === "folder";
  const color = FILE_CLR[item.type] ?? T.textMuted;
  const FIcon = isFolder ? (open ? FolderOpen : Folder) : FileText;

  return (
    <div>
      <div
        onClick={() => isFolder ? setOpen(o => !o) : onOpen && onOpen(item)}
        style={{
          display: "flex", alignItems: "center", gap: 7,
          padding: "6px 8px", borderRadius: 7,
          marginLeft: depth * 13, cursor: "pointer",
          transition: "background 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = T.surfaceHover}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        {isFolder
          ? <ChevronRight size={10} color={T.textDim} style={{ transition: "transform 0.18s", transform: open ? "rotate(90deg)" : "none", flexShrink: 0 }} />
          : <span style={{ width: 10, flexShrink: 0 }} />
        }
        <FIcon size={12} style={{ color, flexShrink: 0 }} />
        <span style={{ fontSize: 11.5, color: T.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
        {!isFolder && <span style={{ fontSize: 10, color: T.textDim, fontFamily: "monospace", flexShrink: 0 }}>{item.size}</span>}
      </div>
      {isFolder && open && item.children?.map(c => <ArchiveItem key={c.id} item={c} depth={depth + 1} onOpen={onOpen} />)}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CENTER: 3D EMBED VIEW
═══════════════════════════════════════════════════════════ */
function PlantView3D() {
  const [mapView, setMapView] = useState("3d");
  const [fs, setFs] = useState(false);

  const VIEWS = {
    "3d":  { label: "3D Visualization",     url: "https://p3d.in/e/9slBW+load+shading,dl,help,share,spin,link-hidden" },
    "map": { label: "360 Virtual Tour", url: "https://dutabasis.com/imahnoong" },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", ...(fs ? { position: "fixed", inset: 0, zIndex: 999, background: T.bg } : {}) }}>
      {/* toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 4, height: 20, borderRadius: 3, background: `linear-gradient(180deg,${T.cyan},${T.indigo})`, boxShadow: `0 0 10px ${T.cyan}88` }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Visualization</span>
          
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ display: "flex", background: "rgba(0,0,0,0.3)", borderRadius: 9, padding: 3, border: `1px solid ${T.border}` }}>
            {Object.entries(VIEWS).map(([k, vw]) => (
              <button key={k} onClick={() => setMapView(k)}
                style={{
                  padding: "4px 12px", borderRadius: 7, border: "none", cursor: "pointer",
                  fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 5,
                  transition: "all 0.18s",
                  background: mapView === k ? `${T.cyan}22` : "transparent",
                  color: mapView === k ? T.cyan : T.textMuted,
                }}>
                {k === "3d" ? <Layers size={11} /> : <Globe size={11} />}
                {vw.label}
              </button>
            ))}
          </div>
          <button onClick={() => setFs(f => !f)}
            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Maximize2 size={13} color={T.textMuted} />
          </button>
        </div>
      </div>

      {/* embed */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        {[["top:12px;left:12px","borderTop","borderLeft"],["top:12px;right:12px","borderTop","borderRight"],["bottom:12px;left:12px","borderBottom","borderLeft"],["bottom:12px;right:12px","borderBottom","borderRight"]].map(([pos,b1,b2],i) => {
          const s = Object.fromEntries(pos.split(";").map(x => { const [k,v]=x.split(":"); return [k.trim(),v.trim()]; }));
          return <div key={i} style={{ position:"absolute",...s,width:18,height:18,zIndex:5,pointerEvents:"none",[b1]:`2px solid ${T.cyan}66`,[b2]:`2px solid ${T.cyan}66` }} />;
        })}
        
        <iframe key={mapView} src={VIEWS[mapView].url} title={VIEWS[mapView].label}
          style={{ width: "100%", height: "100%", border: "none", background: "#0a0f1a" }}
          allow="autoplay; fullscreen; xr-spatial-tracking" allowFullScreen />
      </div>

      {/* status bar */}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CENTER: SENSOR DETAIL VIEW
═══════════════════════════════════════════════════════════ */
function SensorDetail({ sensorKey, liveData, onBack }) {
  const meta = SENSOR_META[sensorKey];
  const [period, setPeriod]         = useState("1d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo]     = useState("");
  const [chartType, setChartType]   = useState("area");

  const baseHistory = HISTORY[sensorKey] ?? liveData ?? [];
  const periodMs    = PERIODS.find(p => p.key === period)?.ms ?? null;
  const filtered    = useMemo(() => filterByPeriod(baseHistory, periodMs, customFrom, customTo), [baseHistory, periodMs, customFrom, customTo]);
  const chartData   = useMemo(() => downsample(filtered, 300), [filtered]);

  const stats = useMemo(() => {
    if (!filtered.length) return {};
    const vals = filtered.map(d => d.v);
    return {
      current: vals[vals.length - 1],
      avg: vals.reduce((a, b) => a + b, 0) / vals.length,
      max: Math.max(...vals),
      min: Math.min(...vals),
      count: vals.length,
    };
  }, [filtered]);

  const xKey = period === "1d" || period === "6h" || period === "1h" ? "time" : period === "custom" ? "datetime" : "date";
  const Icon = meta.icon;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", animation: "fadeUp 0.25s ease" }}>
      {/* back bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onBack}
            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ArrowLeft size={14} color={T.textMuted} />
          </button>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: `${meta.color}1a`, border: `1px solid ${meta.color}44`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon size={15} color={meta.color} />
          </div>
          <div>
            <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{meta.label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
              <Chip color={T.green}><span style={{ width: 5, height: 5, borderRadius: "50%", background: T.green, animation: "pulse-dot 2s infinite", display: "inline-block" }} />LIVE</Chip>
              <span style={{ fontSize: 10, color: T.textMuted, fontFamily: "monospace" }}>{stats.count?.toLocaleString()} data points</span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* chart type toggle */}
          <div style={{ display: "flex", background: "rgba(0,0,0,0.3)", borderRadius: 9, padding: 3, border: `1px solid ${T.border}` }}>
            {[{ k: "area", Icon: Activity }, { k: "line", Icon: TrendingUp }, { k: "bar", Icon: BarChart2 }].map(({ k, Icon: I }) => (
              <button key={k} onClick={() => setChartType(k)}
                style={{ width: 30, height: 28, borderRadius: 7, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s", background: chartType === k ? `${meta.color}22` : "transparent" }}>
                <I size={12} color={chartType === k ? meta.color : T.textMuted} />
              </button>
            ))}
          </div>
          <button style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", fontSize: 11, color: T.textMuted, fontWeight: 600 }}>
            <Download size={11} /> Export
          </button>
        </div>
      </div>

      {/* period selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderBottom: `1px solid ${T.border}`, flexShrink: 0, flexWrap: "wrap" }}>
        <PeriodSelector period={period} onChange={setPeriod} />
        {period === "custom" && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              style={{ padding: "4px 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: "rgba(0,0,0,0.3)", color: T.text, fontSize: 11, fontFamily: "monospace", outline: "none" }} />
            <span style={{ color: T.textMuted, fontSize: 12 }}>→</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              style={{ padding: "4px 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: "rgba(0,0,0,0.3)", color: T.text, fontSize: 11, fontFamily: "monospace", outline: "none" }} />
          </div>
        )}
        <span style={{ fontSize: 11, color: T.textDim, fontFamily: "monospace", marginLeft: "auto" }}>
          {filtered.length > 0 && `${filtered[0].datetime ?? filtered[0].date} — ${filtered.at(-1).datetime ?? filtered.at(-1).date}`}
        </span>
      </div>

      {/* stat pills */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, padding: "12px 16px", flexShrink: 0 }}>
        {[
          { label: "Current", val: stats.current?.toFixed(3), color: meta.color },
          { label: "Average",  val: stats.avg?.toFixed(3),    color: T.indigo },
          { label: "Maximum",  val: stats.max?.toFixed(3),    color: T.red },
          { label: "Minimum",  val: stats.min?.toFixed(3),    color: T.green },
        ].map(s => (
          <div key={s.label} style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 14px" }}>
            <p style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{s.label}</p>
            <p style={{ fontSize: 20, fontWeight: 800, color: s.color, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>
              {s.val ?? "—"} <span style={{ fontSize: 11, fontWeight: 400, color: T.textDim }}>{meta.unit}</span>
            </p>
          </div>
        ))}
      </div>

      {/* main chart */}
      <div style={{ flex: 1, padding: "0 16px 16px", minHeight: 0 }}>
        <GlassCard style={{ height: "100%", padding: "12px 8px 8px 4px" }}>
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "bar" ? (
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey={xKey} tick={{ fill: T.textMuted, fontSize: 9, fontFamily: "monospace" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: T.textMuted, fontSize: 9, fontFamily: "monospace" }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip unit={meta.unit} color={meta.color} />} />
                {meta.threshold && <ReferenceLine y={meta.threshold} stroke={meta.color} strokeDasharray="4 4" strokeOpacity={0.5} label={{ value: "Threshold", fill: meta.color, fontSize: 9 }} />}
                <Bar dataKey="v" fill={meta.color} fillOpacity={0.7} radius={[2, 2, 0, 0]} />
              </BarChart>
            ) : chartType === "line" ? (
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey={xKey} tick={{ fill: T.textMuted, fontSize: 9, fontFamily: "monospace" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: T.textMuted, fontSize: 9, fontFamily: "monospace" }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip unit={meta.unit} color={meta.color} />} />
                {meta.threshold && <ReferenceLine y={meta.threshold} stroke={meta.color} strokeDasharray="4 4" strokeOpacity={0.5} />}
                <Line type="monotoneX" dataKey="v" stroke={meta.color} strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            ) : (
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id={`det_${meta.gradId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={meta.color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={meta.color} stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey={xKey} tick={{ fill: T.textMuted, fontSize: 9, fontFamily: "monospace" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: T.textMuted, fontSize: 9, fontFamily: "monospace" }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip unit={meta.unit} color={meta.color} />} />
                {meta.threshold && <ReferenceLine y={meta.threshold} stroke={meta.color} strokeDasharray="4 4" strokeOpacity={0.5} />}
                <Area type="monotoneX" dataKey="v" stroke={meta.color} strokeWidth={2} fill={`url(#det_${meta.gradId})`} dot={false} isAnimationActive={false} />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </GlassCard>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CENTER: CCTV VIEW
═══════════════════════════════════════════════════════════ */
function CctvView({ cam, onBack }) {
  const [muted, setMuted] = useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", animation: "fadeUp 0.25s ease" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onBack}
            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ArrowLeft size={14} color={T.textMuted} />
          </button>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: `${T.amber}1a`, border: `1px solid ${T.amber}44`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Camera size={15} color={T.amber} />
          </div>
          <div>
            <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>CCTV — {cam.label}</span>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>{cam.zone}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          
        </div>
      </div>

      {/* video area */}
      <div style={{ flex: 1, position: "relative", background: "#000", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {/* Scanlines */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.05, backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(255,255,255,.2) 2px,rgba(255,255,255,.2) 3px)", pointerEvents: "none", zIndex: 2 }} />
          <video
            className="w-full h-full"
            src={cam.link}
            autoPlay
            muted
          />     
        {/* placeholder camera view */}
        {/* <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, opacity: 0.3 }}>
          <Camera size={56} color="#fff" />
          <span style={{ fontSize: 13, color: "#fff", fontFamily: "monospace", letterSpacing: "0.15em" }}>WAITING FOR STREAM — CAM-0{cam.id}</span>
          <span style={{ fontSize: 11, color: "#aaa", fontFamily: "monospace" }}>Configure RTSP/HLS URL to enable live feed</span>
        </div> */}
        {/* HUD overlay corners */}
        {[["top:14px;left:14px","borderTop","borderLeft"],["top:14px;right:14px","borderTop","borderRight"],["bottom:14px;left:14px","borderBottom","borderLeft"],["bottom:14px;right:14px","borderBottom","borderRight"]].map(([pos,b1,b2],i) => {
          const s = Object.fromEntries(pos.split(";").map(x => { const [k,v]=x.split(":"); return [k.trim(),v.trim()]; }));
          return <div key={i} style={{ position:"absolute",...s,width:20,height:20,zIndex:4,pointerEvents:"none",[b1]:`2px solid ${T.amber}88`,[b2]:`2px solid ${T.amber}88` }} />;
        })}
      </div>

      {/* controls */}
      {/* <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderTop: `1px solid ${T.border}`, flexShrink: 0, background: "rgba(0,0,0,0.25)" }}>
        {[
          { Icon: Play,  label: "Play"   },
          { Icon: Pause, label: "Pause"  },
          { Icon: ZoomIn,label: "Zoom"   },
        ].map(({ Icon, label }) => (
          <button key={label}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", fontSize: 12, color: T.textMuted, fontWeight: 600, transition: "all 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.background = T.surfaceHover; e.currentTarget.style.color = T.text; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.surface; e.currentTarget.style.color = T.textMuted; }}>
            <Icon size={13} /> {label}
          </button>
        ))}
        <button onClick={() => setMuted(m => !m)}
          style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", fontSize: 12, color: T.textMuted, fontWeight: 600 }}>
          {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
          {muted ? "Unmute" : "Mute"}
        </button>
      </div> */}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CENTER: ARCHIVE FILE VIEW
═══════════════════════════════════════════════════════════ */
function ArchiveFileView({ file, onBack }) {
  const color = FILE_CLR[file.type] ?? T.textMuted;
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", animation: "fadeUp 0.25s ease" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onBack}
            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ArrowLeft size={14} color={T.textMuted} />
          </button>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: `${color}1a`, border: `1px solid ${color}44`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FileText size={15} color={color} />
          </div>
          <div>
            <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{file.name}</span>
            <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
              <Chip color={color}>{file.type.toUpperCase()}</Chip>
              <span style={{ fontSize: 10, color: T.textMuted, fontFamily: "monospace" }}>{file.size}</span>
            </div>
          </div>
        </div>
      </div>

      {/* preview area */}
      <div className="w-full h-full border-0">
        <div style={{ textAlign: "center", animation: "fadeUp 0.3s ease" }}>
          <iframe
            src={file.link}
            title={file.name}
            className="w-full border-0"
            allow="autoplay; fullscreen; xr-spatial-tracking"
            allowFullScreen
            style={{ background: '#030810' }}
          />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CCTV THUMBNAIL STRIP (shown in default 3D view)
═══════════════════════════════════════════════════════════ */
function CctvStrip({ onOpen }) {
  const VISIBLE = 4;
  const [startIndex, setStartIndex] = useState(0);

  // auto slide tiap 3 detik
  useEffect(() => {
    const interval = setInterval(() => {
      setStartIndex(prev => (prev + 1) % CAMS.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [CAMS.length]);

  // ambil 3 item (looping)
  const visibleCams = Array.from({ length: VISIBLE }, (_, i) => {
    return CAMS[(startIndex + i) % CAMS.length];
  });
  return (
    <div style={{ flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
      <div style={{ padding: "10px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: `${T.amber}1a`, border: `1px solid ${T.amber}44`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Camera size={13} color={T.amber} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>CCTV</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.red, animation: "pulse-dot 1.5s infinite", boxShadow: `0 0 8px ${T.red}` }} />
          <span style={{ fontSize: 11, color: "#fca5a5", fontFamily: "monospace", letterSpacing: "0.07em" }}>CCTV · 9 FEEDS</span>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, padding: "0 16px 14px" }}>
        {visibleCams.map(cam => (
          <div
            key={cam.id}
            onClick={() => onOpen(cam)}
            style={{
              background: "#000",
              border: `1px solid ${T.border}`,
              borderRadius: 10,
              overflow: "hidden",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = `${T.amber}66`;
              e.currentTarget.style.transform = "scale(1.02)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = T.border;
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <div style={{
              aspectRatio: "16/9",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden"
            }}>
              <video className="w-full" src={cam.link} muted />
             
            </div>

            <div style={{
              padding: "5px 8px 6px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <span style={{ fontSize: 14, color: T.textMuted }}>
                {cam.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   RIGHT PANEL — ARCHIVE SIDEBAR
═══════════════════════════════════════════════════════════ */
function ArchiveSidebar({ onOpenFile }) {
  const [search, setSearch] = useState("");

  const filterArchive = (items, q) => {
    if (!q) return items;
    return items.reduce((acc, item) => {
      if (item.type === "folder") {
        const children = filterArchive(item.children ?? [], q);
        if (children.length || item.name.toLowerCase().includes(q.toLowerCase()))
          acc.push({ ...item, children });
      } else if (item.name.toLowerCase().includes(q.toLowerCase())) {
        acc.push(item);
      }
      return acc;
    }, []);
  };

  const filtered = filterArchive(ARCHIVE, search);

  return (
    <GlassCard style={{ display: "flex", flexDirection: "column", overflow: "hidden", height: "100%" }}>
      {/* header */}
      <div style={{ padding: "14px 14px 10px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: `${T.amber}18`, border: `1px solid ${T.amber}44`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Folder size={13} color={T.amber} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Archives</span>
          </div>
          <span style={{ fontSize: 10, color: T.textDim, background: "rgba(255,255,255,0.06)", padding: "2px 8px", borderRadius: 6, fontFamily: "monospace" }}>
            {ARCHIVE.length}
          </span>
        </div>
        <input
          type="text" placeholder="Search files…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: "100%", padding: "7px 12px", borderRadius: 9, fontSize: 12,
            background: "rgba(0,0,0,0.3)", border: `1px solid ${T.border}`,
            color: T.text, outline: "none", fontFamily: "'DM Sans', sans-serif",
            transition: "border-color 0.15s",
          }}
          onFocus={e => e.target.style.borderColor = `${T.amber}66`}
          onBlur={e => e.target.style.borderColor = T.border}
        />
      </div>

      {/* file tree */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 8px" }}>
        {filtered.map(item => <ArchiveItem key={item.id} item={item} onOpen={onOpenFile} />)}
      </div>

      {/* storage */}
      <div style={{ padding: "10px 14px", borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
          <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em" }}>Storage</span>
          <span style={{ fontSize: 11, color: T.text, fontFamily: "monospace" }}>4.2 / 20 GB</span>
        </div>
        <div style={{ height: 5, background: "rgba(255,255,255,0.08)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ height: "100%", width: "21%", borderRadius: 4, background: `linear-gradient(90deg,${T.cyan},${T.indigo})`, boxShadow: `0 0 8px ${T.cyan}55`, transition: "width 0.8s ease" }} />
        </div>
        <p style={{ fontSize: 10, color: T.textDim, marginTop: 5 }}>21% used · 15.8 GB free</p>
      </div>
    </GlassCard>
  );
}

/* ═══════════════════════════════════════════════════════════
   ROOT DASHBOARD
═══════════════════════════════════════════════════════════ */
export default function DetailWaterFlow() {
  // Live rolling data (latest 40 pts)
  const [live, setLive] = useState({
    water_flow:  HISTORY.water_flow.slice(-40),
    water_level: HISTORY.water_level.slice(-40),
    temperature: HISTORY.temperature.slice(-40),
  });
  const [wq, setWq]   = useState({ pH: 7.1, turbidity: 3.2, dissolved_oxygen: 8.4, conductivity: 412 });
  const [online,  setOnline]  = useState(true);
  const [loading, setLoading] = useState(false);
  const [lastUpd, setLastUpd] = useState(new Date());
  const [alerts,  setAlerts]  = useState(0);

  // Center panel state
  // mode: "plant" | "sensor" | "cctv" | "file"
  const [centerMode, setCenterMode]     = useState("plant");
  const [activeSensor, setActiveSensor] = useState(null);
  const [activeCam, setActiveCam]       = useState(null);
  const [activeFile, setActiveFile]     = useState(null);

  const tickRef = useRef(null);

  const push = (arr, val) => {
    const now = new Date();
    return [...arr.slice(-39), {
      ts: now.getTime(),
      t: now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }),
      time: now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }),
      date: now.toLocaleDateString("id-ID", { day: "2-digit", month: "short" }),
      datetime: now.toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
      v: +val.toFixed(3),
    }];
  };
  const drift = (arr, base, step) => {
    const last = arr.at(-1)?.v ?? base;
    return push(arr, Math.max(0, last + (Math.random() - 0.495) * step));
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.allSettled([
        fetchSensor("1", "water_flow"),
        fetchSensor("1", "water_level"),
        fetchSensor("1", "temperature"),
        fetchSensor("1", "water_quality"),
      ]);
      setLive(p => ({
        water_flow:  r1.status === "fulfilled" && r1.value?.value != null ? push(p.water_flow, r1.value.value) : drift(p.water_flow, 24, 1.2),
        water_level: r2.status === "fulfilled" && r2.value?.value != null ? push(p.water_level, r2.value.value) : drift(p.water_level, 4.2, 0.18),
        temperature: r3.status === "fulfilled" && r3.value?.value != null ? push(p.temperature, r3.value.value) : drift(p.temperature, 28.4, 0.35),
      }));
      setWq(p => r4.status === "fulfilled" && r4.value ? { ...p, ...r4.value } : p);
      setOnline(true);
    } catch { setOnline(false); }
    finally { setLoading(false); setLastUpd(new Date()); }
  }, []);

  useEffect(() => {
    let n = 0;
    if ((live.water_flow.at(-1)?.v  ?? 0) > 35)  n++;
    if ((live.water_level.at(-1)?.v ?? 0) > 5.5) n++;
    if ((live.temperature.at(-1)?.v ?? 0) > 32)  n++;
    setAlerts(n);
  }, [live]);

  useEffect(() => {
    fetchAll();
    tickRef.current = setInterval(fetchAll, 30000);
    return () => clearInterval(tickRef.current);
  }, []); // eslint-disable-line

  // Navigation helpers
  const openSensor = key => { setActiveSensor(key); setCenterMode("sensor"); };
  const openCam    = cam  => { setActiveCam(cam);    setCenterMode("cctv");  };
  const openFile   = file => { setActiveFile(file);  setCenterMode("file");  };
  const goBack     = ()   => { setCenterMode("plant"); };

  return (
    <>
      <style>{GLOBAL_STYLE}</style>

      {/* Ambient glow */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "10%", left: "5%", width: 500, height: 500, background: "radial-gradient(circle, rgba(56,189,248,0.055) 0%, transparent 70%)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", bottom: "15%", right: "5%", width: 400, height: 400, background: "radial-gradient(circle, rgba(251,191,36,0.045) 0%, transparent 70%)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", top: "50%", left: "42%", width: 350, height: 350, background: "radial-gradient(circle, rgba(129,140,248,0.04) 0%, transparent 70%)", borderRadius: "50%" }} />
      </div>

      <div style={{ height: "100vh", display: "flex", flexDirection: "column", position: "relative", zIndex: 1 }}>

        

        {/* ══════════ 3-COLUMN BODY ══════════ */}
        <div style={{ flex: 1, display: "flex", gap: 14, padding: "14px 18px", overflow: "hidden", minHeight: 0 }}>

          {/* ── CENTER 4/6 ── */}
          <main style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            <GlassCard style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              
                <SensorDetail sensorKey={"water_flow"} liveData={live["water_flow"]} onBack={goBack} />
              
            </GlassCard>
          </main>

        </div>
      </div>
    </>
  );
}